import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Comment, Task, TaskAttachment, TaskEvent, TaskStatus } from '../lib/types';

export function useProjectTasks(slug: string | undefined) {
  return useQuery({
    queryKey: ['project-tasks', slug],
    queryFn: () =>
      api<{ tasks: Task[] }>(`/api/projects/${slug}/tasks`).then((r) => r.tasks),
    enabled: Boolean(slug),
    refetchInterval: 10_000,
  });
}

export function useTask(taskId: string | undefined) {
  return useQuery({
    queryKey: ['task', taskId],
    queryFn: () => api<{ task: Task }>(`/api/tasks/${taskId}`).then((r) => r.task),
    enabled: Boolean(taskId),
  });
}

export function useTaskEvents(taskId: string | undefined) {
  return useQuery({
    queryKey: ['task-events', taskId],
    queryFn: () =>
      api<{ events: TaskEvent[] }>(`/api/tasks/${taskId}/events`).then((r) => r.events),
    enabled: Boolean(taskId),
  });
}

export function useTaskComments(taskId: string | undefined) {
  return useQuery({
    queryKey: ['task-comments', taskId],
    queryFn: () =>
      api<{ comments: Comment[] }>(`/api/tasks/${taskId}/comments`).then((r) => r.comments),
    enabled: Boolean(taskId),
  });
}

export interface CreateTaskInput {
  prompt: string;
  title?: string;
  force_approval?: boolean;
  base_branch?: string;
  /** Unattended mode: pipeline resolves questions + auto-approves (risk-gated). */
  autopilot?: boolean;
  /**
   * Per-task v2 config override (partial config.jsonc) — cost ceiling,
   * runtime cap, blast-radius/drift policy, autopilot policy. Built by
   * NewTaskForm's "Advanced" controls; the worker forwards it to v2 as
   * --config-override. Omitted when the user changes nothing.
   */
  config_override?: Record<string, unknown>;
}

export function useCreateTask(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskInput) =>
      api<{ task: Task }>(`/api/projects/${slug}/tasks`, {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.task),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-tasks', slug] }),
  });
}

export interface AddCommentInput {
  body: string;
  /** Default true on a completed task; false = comment only, no follow-up. */
  triggers_continuation?: boolean;
}

export function useAddComment(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddCommentInput) =>
      api<{ comment: Comment; triggered_task: Task | null }>(
        `/api/tasks/${taskId}/comments`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task-comments', taskId] });
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
      qc.invalidateQueries({ queryKey: ['task-chain', taskId] });
    },
  });
}

/**
 * Re-queue a failed task. Backend increments retry_count + flips
 * status to 'queued'; the worker spawns `pipeline replan --yes` so
 * Architect produces a revised plan for the remaining work and
 * auto-executes from there.
 */
export function useRetryTask(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean; retry_count: number }>(`/api/tasks/${taskId}/retry`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task', taskId] });
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
    },
  });
}

/**
 * Cancel a task. Works on running, queued, pending_approval, or held
 * tasks. The worker stops the pipeline subprocess on its next poll
 * (within ~5s). Already-terminal tasks return 409.
 */
export function useCancelTask(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>(`/api/tasks/${taskId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task', taskId] });
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
    },
  });
}

/** Move a queued task to 'held' so the worker skips it. */
export function useHoldTask(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>(`/api/tasks/${taskId}/hold`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task', taskId] });
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
    },
  });
}

/** Move a held task back to 'queued' so the worker can pick it up. */
export function useUnholdTask(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>(`/api/tasks/${taskId}/unhold`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task', taskId] });
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
    },
  });
}

/** Hard delete a non-running task (cascades comments + events). */
export function useDeleteTask(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>(`/api/tasks/${taskId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
    },
  });
}

/**
 * Submit user feedback on a pending plan so the architect produces a
 * revised plan. Backend stores feedback + flips status to 'queued'; the
 * worker re-spawns as `pipeline replan --feedback ... --yes`.
 */
export function useRefinePlan(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (feedback: string) =>
      api<{ ok: boolean }>(`/api/tasks/${taskId}/refine-plan`, {
        method: 'POST',
        body: JSON.stringify({ feedback }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task', taskId] });
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
    },
  });
}

/**
 * Submit answers to the pipeline's clarifying questions. The backend
 * flips status back to 'queued' so the worker re-spawns the pipeline
 * with the answers baked into `pipeline resume --answers`.
 */
export function useAnswerQuestions(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      answers: Array<{ question_id: string; answer: string; custom?: boolean }>,
    ) =>
      api<{ ok: boolean }>(`/api/tasks/${taskId}/answer-questions`, {
        method: 'POST',
        body: JSON.stringify({ answers }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task', taskId] });
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
    },
  });
}

export function useApproveTask(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { approved: boolean; reason?: string }) =>
      api<{ ok: boolean }>(`/api/tasks/${taskId}/approve`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task', taskId] });
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
    },
  });
}

// Used by board for column grouping. Ordered to read left-to-right as
// the task lifecycle: queued → planning → review gates → execution →
// terminal. The two pending_* gates sit between planning and running.
// pending_questions_in_execution sits next to running because it's the
// execution-pool mid-step pause (Phase 5 severity rule).
export const COLUMN_ORDER: TaskStatus[] = [
  'queued',
  'held',
  'planning',
  'pending_questions',
  'pending_approval',
  'running',
  'pending_questions_in_execution',
  'completed',
  'failed',
  'cancelled',
];

// ─── Task attachments (Phase 11) ──────────────────────────────────

export function useTaskAttachments(taskId: string | undefined) {
  return useQuery({
    queryKey: ['task-attachments', taskId],
    queryFn: () =>
      api<{ attachments: TaskAttachment[] }>(`/api/tasks/${taskId}/attachments`).then(
        (r) => r.attachments,
      ),
    enabled: Boolean(taskId),
  });
}

/**
 * Upload one or more files. multipart/form-data — does NOT use the
 * `api()` helper because that always JSON-serialises the body.
 */
export function useUploadAttachments(taskId: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]) => {
      if (files.length === 0) return { attachments: [] as TaskAttachment[] };
      const form = new FormData();
      for (const f of files) form.append('files', f, f.name);
      const res = await fetch(`/api/tasks/${taskId}/attachments`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      const body = (await res.json().catch(() => ({}))) as {
        attachments?: TaskAttachment[];
        error?: string;
        message?: string;
      };
      if (!res.ok) {
        throw new Error(body.message ?? body.error ?? `upload failed (HTTP ${res.status})`);
      }
      return { attachments: body.attachments ?? [] };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task-attachments', taskId] });
      qc.invalidateQueries({ queryKey: ['project-tasks', projectSlug] });
    },
  });
}

export function useDeleteAttachment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      api<{ ok: boolean }>(`/api/tasks/${taskId}/attachments/${attachmentId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['task-attachments', taskId] }),
  });
}

export function groupByStatus(tasks: Task[]): Record<TaskStatus, Task[]> {
  const out: Record<TaskStatus, Task[]> = {
    queued: [],
    planning: [],
    pending_approval: [],
    pending_questions: [],
    pending_questions_in_execution: [],
    running: [],
    held: [],
    completed: [],
    failed: [],
    cancelled: [],
  };
  for (const t of tasks) {
    out[t.status].push(t);
  }
  return out;
}
